import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from 'react';
import { authService } from '../services/auth';
import { Folder, AudioFile, User } from '../types';

interface AppState {
  user: User | null;
  theme: 'light' | 'dark';
  isLoading: boolean;
  isAuthenticating: boolean;
  selectedAudioFile: AudioFile | null;
  selectedFolder: Folder | null;
}

type AppAction =
  | { type: 'SET_USER'; payload: User }
  | { type: 'CLEAR_USER' }
  | { type: 'TOGGLE_THEME' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_AUTHENTICATING'; payload: boolean }
  | { type: 'SET_SELECTED_AUDIO_FILE'; payload: AudioFile | null }
  | { type: 'SET_SELECTED_FOLDER'; payload: Folder | null };

const initialState: AppState = {
  user: null,
  theme: 'light',
  isLoading: false,
  isAuthenticating: true,
  selectedAudioFile: null,
  selectedFolder: null,
};

const AppContext = createContext<
  | {
      state: AppState;
      dispatch: React.Dispatch<AppAction>;
    }
  | undefined
>(undefined);

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticating: false };
    case 'CLEAR_USER':
      return { ...state, user: null, isAuthenticating: false };
    case 'TOGGLE_THEME':
      return { ...state, theme: state.theme === 'light' ? 'dark' : 'light' };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_AUTHENTICATING':
      return { ...state, isAuthenticating: action.payload };
    case 'SET_SELECTED_AUDIO_FILE':
      return {
        ...state,
        selectedAudioFile: action.payload,
        selectedFolder: null,
      };
    case 'SET_SELECTED_FOLDER':
      return {
        ...state,
        selectedFolder: action.payload,
        selectedAudioFile: null,
      };
    default:
      return state;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      const startTime = Date.now();
      const minLoadingTime = 1500; // Show loading for at least 1.5 seconds

      const currentUser = authService.getCurrentUser();
      if (currentUser && authService.isAuthenticated()) {
        try {
          const validation = await authService.validateToken();

          // Ensure minimum loading time
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

          setTimeout(() => {
            if (validation.valid && validation.user) {
              dispatch({ type: 'SET_USER', payload: validation.user });
            } else {
              dispatch({ type: 'CLEAR_USER' });
            }
          }, remainingTime);
        } catch (error) {
          console.error('Token validation failed:', error);
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

          setTimeout(() => {
            dispatch({ type: 'CLEAR_USER' });
          }, remainingTime);
        }
      } else {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

        setTimeout(() => {
          dispatch({ type: 'SET_AUTHENTICATING', payload: false });
        }, remainingTime);
      }
    };

    initializeAuth();
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
